import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const Blog28032026 = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('your-mine-unearthing-its-full-potential');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    {
        "id": "context",
        "title": "1. Context",
        "originalTitle": "Context"
    },
    {
        "id": "problem-statement",
        "title": "2. Problem Statement",
        "originalTitle": "Problem Statement"
    },
    {
        "id": "core-framework",
        "title": "3. Core Framework",
        "originalTitle": "Core Framework"
    },
    {
        "id": "comparative-analysis",
        "title": "4. Comparative Analysis",
        "originalTitle": "Comparative Analysis"
    },
    {
        "id": "business-use-cases",
        "title": "5. Business Use Cases",
        "originalTitle": "Business Use Cases"
    },
    {
        "id": "benefits-outcomes",
        "title": "6. Benefits & Outcomes",
        "originalTitle": "Benefits & Outcomes"
    },
    {
        "id": "challenges-realities",
        "title": "7. Challenges & Realities",
        "originalTitle": "Challenges & Realities"
    },
    {
        "id": "future-outlook",
        "title": "8. Future Outlook",
        "originalTitle": "Future Outlook"
    },
    {
        "id": "conclusion",
        "title": "9. Conclusion",
        "originalTitle": "Conclusion"
    },
    {
        "id": "call-to-action",
        "title": "10. Call to Action",
        "originalTitle": "Call to Action"
    }
];

  const faqData = [
    {
        "question": "What is this technology and how does it work?",
        "answer": "This technology represents a significant advancement in the field, offering innovative solutions to common challenges through modern approaches and proven methodologies."
    },
    {
        "question": "Who can benefit from implementing this solution?",
        "answer": "Organizations of all sizes can benefit, particularly those looking to improve efficiency, reduce costs, and enhance their competitive advantage through technological innovation."
    },
    {
        "question": "What are the main challenges in implementation?",
        "answer": "Key challenges include initial setup complexity, integration with existing systems, and ensuring proper training. However, with proper planning and support, these can be effectively managed."
    },
    {
        "question": "What ROI can be expected?",
        "answer": "While results vary by organization, typical implementations show significant improvements in operational efficiency, cost reduction, and enhanced capabilities within the first year."
    }
];

  const tldrPoints = [
    "The global mining industry stands at a transformative crossroads, driven by an insatiable demand for industrial minerals and metals and a relentless p...",
    "Traditional mining operations often grapple with a myriad of challenges that lead to significant operational inefficiencies and escalating costs. Thes...",
    "Advanced Analytics refers to sophisticated techniques and tools that go beyond traditional business intelligence to interpret large, complex datasets...."
];

  const keyTakeaways = [
    "Fast implementation strategies deliver measurable ROI within weeks, not months",
    "Agile methodologies reduce time-to-production by 60-80% compared to traditional approaches",
    "Cloud-native architecture enables rapid scaling without infrastructure bottlenecks",
    "Automated workflows eliminate manual bottlenecks and accelerate delivery timelines",
    "Real-time analytics provide immediate insights for faster decision-making"
];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Is Your Mine Unearthing Its Full Potential? The Power of Advanced Analytics in Mining Engineering!",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".tldr-section", ".key-takeaways-section"]
        }
      },
      {
        "@type": "HowTo",
        "name": blog?.title,
        "description": blog?.excerpt,
        "step": keyTakeaways.map((takeaway, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": `Step ${index + 1}`,
          "text": takeaway
        }))
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.publishDate}</span>
              </div>
            </div>

            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

              {/* TL;DR Section - AEO Optimized */}
              <div className="tldr-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>TL;DR</span>
                  Quick Summary
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {tldrPoints.map((point, idx) => (
                    <li key={idx} style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.25rem',
                        lineHeight: '1'
                      }}>•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Context
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The global mining industry stands at a transformative crossroads, driven by an insatiable demand for industrial minerals and metals and a relentless pursuit of operational excellence.  This "Digital Transformation" is not just a buzzword; it's a strategic imperative, with technologies like Artificial Intelligence (AI) and the Internet of Things (IoT) becoming foundational requirements.   Advanced Analytics in Mining Engineering is revolutionizing every stage of the mine value chain, from predicting geological formations in exploration to optimizing logistics and marketing, by turning vast datasets into actionable insights.  
              </p>

              <h2 id="problem-statement" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Problem Statement
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Traditional mining operations often grapple with a myriad of challenges that lead to significant operational inefficiencies and escalating costs. These include:
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                High Operational Costs: Rising energy costs, which can represent up to 30% of site operating expenses, and the high cost of labor, particularly in hazardous or remote locations.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Unplanned Downtime: Equipment failures leading to costly interruptions, with downtime averaging $260,000 per hour, costing the sector an estimated $187 billion annually. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Low Productivity & Resource Utilization: Inefficient processes, bottlenecks in the supply chain, and sub-optimal extraction strategies can decrease the production rate and Overall Equipment Effectiveness (OEE).  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Safety Risks: Manual interventions in dangerous environments contribute to a high accident and incident rate. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Environmental Impact: Difficulty in accurately monitoring and managing energy consumption, water usage, and emissions leads to sustainability challenges and compliance risks. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Data Overload, Insight Underload: Despite collecting massive amounts of data, many companies struggle to leverage it effectively for informed decision-making. 
              </li>
              </ul>

              <h2 id="core-framework" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Advanced Analytics refers to sophisticated techniques and tools that go beyond traditional business intelligence to interpret large, complex datasets.  It encompasses data mining, machine learning (ML), predictive modeling, and AI to uncover hidden patterns, forecast future trends, and derive actionable insights. 
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Advanced Analytics in mining leverages vast streams of data from various sources:
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                1.  Data Collection: Sensors embedded in machinery, IoT devices, geological surveys, satellite imagery, drones, and historical records constantly feed information into a central system.  
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                2.  Data Processing & Cleansing: Raw, often noisy and inconsistent data is aggregated, cleaned, and structured to ensure accuracy and usability. 
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                3.  Algorithmic Analysis: Machine learning and AI algorithms (e.g., classification, clustering, regression analysis) are applied to identify patterns, relationships, and anomalies that human analysis might miss. 
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                4.  Predictive & Prescriptive Insights: These algorithms predict future events (e.g., equipment failure, ore grade variations) and prescribe optimal actions (e.g., maintenance schedules, process adjustments). Digital twins virtual replicas of mining processes enable simulation and optimization.  
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                5.  Real-Time Decision-Making: Insights are delivered to managers and automated systems, enabling faster, more accurate operational adjustments and strategic planning. 
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                While powerful, advanced analytics faces hurdles:
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Data Quality: Incomplete, inaccurate, or inconsistent data can derail projects. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Skill Gaps: A shortage of professionals who combine mining engineering knowledge with data science expertise. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Infrastructure & Integration: Deploying and maintaining the necessary IT infrastructure and integrating it with legacy systems can be complex and costly. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Change Management: Overcoming traditional mindsets that favor "scene and response" over "predict and optimize." 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Ethical Considerations: Ensuring fairness in algorithms and protecting data privacy. 
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Core Framework
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Core Framework"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of core framework concepts and implementation strategies.
                </p>
              </div>

              <h2 id="comparative-analysis" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis
              </h2>

              <div style={{
                overflowX: 'auto',
                margin: '2rem 0',
                border: '1px solid #262626',
                borderRadius: '8px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'clamp(0.875rem, 1.8vw, 1rem)',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  <thead>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Feature
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Traditional Analytics
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Advanced Analytics
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Focus
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Retrospective (What happened?)
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Proactive &amp; Future-oriented (What will happen? How can we make it happen?)
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Data Type
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Primarily structured, historical data
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Structured, unstructured, and real-time data from diverse sources
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Techniques Used
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Basic statistical analysis, descriptive reporting, dashboards
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Machine Learning, AI, Predictive Modeling, Data Mining, Simulation, Optimization
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Insights
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Describes past trends, identifies patterns
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Predicts future outcomes, prescribes optimal actions, identifies causal relationships
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Decision-Making
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Informed decisions based on historical performance
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Data-driven, real-time, strategic, and tactical decision support
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Complexity
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Lower
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Higher (requires specialized skills and infrastructure)
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Cost
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Generally lower for tools and implementation
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Higher initial investment in technology, personnel, and infrastructure
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 id="business-use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Business Use Cases
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: High costs and low success rates for identifying viable mineral deposits. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Advanced analytics accelerates mineral exploration by analyzing geological, geochemical, and geophysical data to pinpoint promising deposits.  This reduces costly trial-and-error drilling and increases discovery rates. For example, AI has helped identify high-grade copper mines, significantly shortening exploration timelines. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Bottlenecks, inefficiencies, and sub-optimal ore grade utilization impacting production rate and Cost per Ton of Material Moved.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Process optimization and simulation modeling identify bottlenecks, optimize parameters (e.g., grinding and flotation processes), and improve overall production efficiency, potentially leading to throughput improvements of 10-15%.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Unplanned equipment breakdowns lead to significant equipment downtime and high repair costs. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Predictive maintenance, driven by analyzing sensor data and historical records, forecasts equipment failures before they occur.  This reduces unplanned downtime by up to 30%, extends equipment lifespan by 20%, and offers maintenance cost savings of 12-25%.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Hazardous working conditions and high accident and incident rates. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Real-time monitoring from IoT sensors, wearables, and environmental systems identifies potential safety hazards (e.g., gas levels, seismic activity, air quality) and alerts personnel, leading to proactive prevention and an expected reduction in workplace incidents by up to 30%.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Inefficient inventory management, high transportation costs, and lack of end-to-end visibility.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Integrated supply chain data from suppliers, transport, and production systems optimizes inventory levels, reduces transportation costs, and improves overall efficiency. Predictive analytics aids in demand forecasting, minimizing stockouts.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: High energy consumption, significant environmental impact, and challenges in meeting ESG (Environmental, Social, and Governance) standards.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Advanced analytics optimizes energy usage by monitoring energy-intensive processes and recommending energy-saving measures.  It also helps track emissions, water usage, and waste, facilitating sustainable practices and compliance with regulatory requirements.  AI-driven energy management is expected to be standard by 2030 to reach "Net Zero" targets. 
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Business Use Cases
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/590020/pexels-photo-590020.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Business Use Cases"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of business use cases concepts and implementation strategies.
                </p>
              </div>

              <h2 id="benefits-outcomes" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Benefits & Outcomes
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Real-time Data Processing: Ability to collect, analyze, and act on data instantaneously from millions of sensor signals. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Predictive Accuracy: Machine learning models provide highly precise forecasts for various operational parameters, from equipment wear to ore grades. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Integrated Insights: Consolidates data from disparate systems (plant history, maintenance, mine planning, logistics) for a holistic view. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Automation Capabilities: Enables autonomous systems and intelligent process controls, reducing human error. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Digital Twin Functionality: Virtual replicas allow for scenario testing and optimization before physical implementation, enhancing planning accuracy. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Increased Productivity: AI has the potential to increase mining productivity by 10-15%. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Significant Cost Reduction: Lower operational and maintenance expenses, with some autonomous systems leading to a 15% reduction in operating costs, and an EBITDA uplift of 10-20% from improved quality.  
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Safety: Up to a 30% reduction in workplace incidents through real-time hazard detection and autonomous operations. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Improved Decision-Making: Data-driven insights support better allocation of labor, equipment, and materials, optimizing production schedules and reducing waste. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Environmental Sustainability: Optimized energy usage and reduced carbon intensity per tonne of ore processed. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Competitive Advantage: Positions companies at the forefront of innovation, driving smarter, safer, and more sustainable operations. 
              </li>
              </ul>

              <h2 id="challenges-realities" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Realities
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Implementing Advanced Analytics in mining is not without its complexities:
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Data Integrity: The biggest hurdle is often the "dirty data" incomplete, inaccurate, or inconsistent sensor signals which can cause predictive maintenance programs to fail and digital twins to lose credibility. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Talent Scarcity: A significant barrier is the lack of bright and trained people who possess both mining engineering knowledge and data analysis skills. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Integration with Legacy Systems: Modern analytics solutions need to seamlessly integrate with existing SCADA systems and historians, which can be a complex undertaking. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Initial Investment: While long-term benefits are substantial, the upfront costs for setting up infrastructure, developing algorithms, and training personnel can be considerable. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Organizational Resistance: Overcoming ingrained habits and fostering a data-driven culture requires strong change management and leadership buy-in. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Scalability: Moving from pilot projects to large-scale implementation across diverse mining operations requires robust strategies and platforms. 
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Challenges & Realities
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Challenges & Realities"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of challenges & realities concepts and implementation strategies.
                </p>
              </div>

              <h2 id="future-outlook" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The next 12 months will solidify Advanced Analytics and AI as non-negotiable tools for the mining industry.  The global AI in mining market, valued at approximately USD 35.47 billion in 2025, is projected to reach USD 828.33 billion by 2034, with a compound annual growth rate (CAGR) of 41.92%.  Key trends include:
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI as a Foundational Requirement: AI will transition from a niche luxury to a core operational necessity across the global sector. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Dominance of Predictive Maintenance: Expect to see predictive maintenance powered by AI-driven analytics dominate asset management, targeting significant reductions in unplanned downtime. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Autonomous Systems Expansion: Continued growth in autonomous haulage systems and other AI-enabled machinery will reduce the need for human presence in hazardous areas and boost efficiency. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                ESG and Sustainability Focus: AI will become the primary tool for monitoring tailings dam stability, carbon emissions, and ensuring environmental compliance. 
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Increased Cloud Adoption: The cloud segment is expected to grow at the fastest CAGR (42.9% from 2025 to 2033), driven by the need for greater operational efficiency and real-time data insights, especially for remote operations. 
              </li>
              </ul>

              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Advanced Analytics, underpinned by AI and Machine Learning, is undeniably transforming the mining industry. It empowers companies to transcend traditional operational limitations, unlocking unprecedented levels of efficiency, safety, and sustainability across the entire value chain. By moving from reactive to proactive strategies, mining companies can significantly reduce costs, enhance productivity, protect their workforce, and minimize environmental impact. The shift towards data-driven decision-making is not just an upgrade; it's the future of mining.
              </p>

              <h2 id="call-to-action" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Call to Action
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Ready to transform your mining operations with the power of data and AI? Contact us today for a Proof of Concept (POC) or a consultation to explore how Advanced Analytics can revolutionize your mine value chain, driving smarter, safer, and more sustainable decisions.
              </p>


              {/* Key Takeaways Section - AEO Optimized */}
              <div className="key-takeaways-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginTop: '3rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>⚡</span>
                  Key Takeaways - Fast Implementation Insights
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} style={{
                      color: '#ffffff',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(20, 184, 166, 0.1)'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(20, 184, 166, 0.2)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Business?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Contact us today for a personalized consultation and discover how we can help you achieve your goals.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Blog28032026;
